import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/homeData';
import { categoryArticles } from '@/mocks/articleData';
import type { ArticleDetail } from '@/mocks/articleData';

const categoryNames: Record<string, string> = {
  honeymoon: '蜜月行程',
  couple: '情侣度假',
  proposal: '求婚之旅',
  'free-travel': '自由行线路',
  'group-tour': '跟团游',
  photo: '旅拍攻略',
};

const categoryArticleImages: Record<string, string> = {
  'sunset-romance-3day': 'https://readdy.ai/api/search-image?query=Luxury%20overwater%20villa%20at%20sunset%20on%20a%20tropical%20island%2C%20private%20infinity%20pool%20overlooking%20calm%20turquoise%20ocean%2C%20wooden%20deck%20with%20candlelit%20dinner%20setup%20for%20two%2C%20warm%20golden%20hour%20light%2C%20romantic%20and%20exclusive%20atmosphere%2C%20luxury%20travel%20photography&width=800&height=500&seq=cat-article-sunset-3day&orientation=landscape',
  'best-season-guide': 'https://readdy.ai/api/search-image?query=Tropical%20island%20landscape%20showing%20different%20seasons%20blended%20together%2C%20sunny%20blue%20sky%20with%20lush%20green%20palm%20trees%2C%20turquoise%20ocean%20water%2C%20travel%20magazine%20cover%20style%2C%20warm%20and%20inviting%20atmosphere%2C%20professional%20landscape%20photography&width=800&height=500&seq=cat-article-season-guide&orientation=landscape',
  'island-romance-5day': 'https://readdy.ai/api/search-image?query=Couple%20snorkeling%20in%20crystal%20clear%20tropical%20water%20with%20colorful%20coral%20reef%20and%20tropical%20fish%2C%20sunlight%20beams%20penetrating%20through%20water%20surface%2C%20vibrant%20underwater%20scene%2C%20paradise%20island%20view%2C%20professional%20underwater%20photography&width=800&height=500&seq=cat-article-island-5day&orientation=landscape',
  'top10-sunset-spots': 'https://readdy.ai/api/search-image?query=Breathtaking%20tropical%20sunset%20over%20calm%20ocean%20with%20vibrant%20orange%20pink%20and%20purple%20sky%2C%20silhouette%20of%20palm%20trees%20and%20rock%20formations%2C%20reflection%20on%20water%20surface%2C%20serene%20and%20romantic%20mood&width=800&height=500&seq=cat-article-sunset-spots&orientation=landscape',
  'lifetime-proposal-4day': 'https://readdy.ai/api/search-image?query=Elegant%20beach%20proposal%20at%20sunset%20with%20rose%20petals%20arranged%20in%20heart%20shape%20on%20white%20sand%2C%20candles%20and%20fairy%20lights%2C%20diamond%20ring%2C%20ocean%20view%2C%20romantic%20atmosphere%2C%20cinematic%20photography&width=800&height=500&seq=cat-article-proposal-4day&orientation=landscape',
  'free-spirit-7day': 'https://readdy.ai/api/search-image?query=Convertible%20car%20driving%20along%20scenic%20coastal%20road%20on%20tropical%20island%2C%20turquoise%20ocean%20on%20one%20side%2C%20lush%20green%20hills%20on%20the%20other%2C%20bright%20sunny%20day%2C%20sense%20of%20freedom%20and%20adventure&width=800&height=500&seq=cat-article-free-7day&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'honeymoon');
  const [articles, setArticles] = useState<ArticleDetail[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setArticles(categoryArticles[slug] || []);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = categoryNames[activeSlug] || activeSlug;

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `爱情岛${categoryName} - 爱情岛线路`,
    description: currentCategory?.description || `爱情岛${categoryName}攻略，精选线路推荐`,
    url: `https://lfjzfp.cn/category/${activeSlug}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <header>
        <Navbar />
      </header>

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100">
          <div className="container-wide py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName },
              ]}
            />

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-3">
              爱情岛{categoryName}
            </h1>
            {currentCategory && (
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
                {currentCategory.description}
              </p>
            )}
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200 sticky top-16 md:top-18 z-30">
          <div className="container-wide">
            <div className="flex items-center gap-1 overflow-x-auto py-2 no-scrollbar">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50/70'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="container-wide section-padding">
          {articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-200 hover:bg-background-200/70"
                >
                  <div className="relative h-44 md:h-48 overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                      style={{
                        backgroundImage: `url('${categoryArticleImages[article.slug] || categoryArticleImages['sunset-romance-3day']}')`,
                      }}
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h2 className="text-base font-bold font-heading text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-xs text-foreground-500 mb-3 line-clamp-2 leading-relaxed">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-background-200 text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-time-line text-[10px]"></i>
                        </span>
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500 text-sm mb-4">该分类暂无文章，敬请期待更多内容上线</p>
              <Link
                to="/"
                className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}